import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type ExportPdpsCsvV1WorkspacesWorkspaceIdPdpsExportGetData,
  type ExportPdpsCsvV1WorkspacesWorkspaceIdPdpsExportGetError,
  type ExportPdpsCsvV1WorkspacesWorkspaceIdPdpsExportGetResponse,
  exportPdpsCsvV1WorkspacesWorkspaceIdPdpsExportGet,
} from '../../generated';

export type ExportPdpsCsvQueryVariables = ExportPdpsCsvV1WorkspacesWorkspaceIdPdpsExportGetData['path'] &
  ExportPdpsCsvV1WorkspacesWorkspaceIdPdpsExportGetData['query'];

/**
 * Response can be either the raw Blob data or an object containing
 * the data along with custom headers (like content_disposition)
 */
export type ExportPdpsCsvQuery =
  | ExportPdpsCsvV1WorkspacesWorkspaceIdPdpsExportGetResponse
  | {
      data: ExportPdpsCsvV1WorkspacesWorkspaceIdPdpsExportGetResponse;
      content_disposition?: string;
      [key: string]: ExportPdpsCsvV1WorkspacesWorkspaceIdPdpsExportGetResponse | string | undefined;
    };

/**
 * Extracts custom vizit-* headers from response and converts them to an object
 */
function getCustomHeaders(response: Response): Record<string, string> | undefined {
  const headers = Object.fromEntries(response.headers);
  const hasCustomHeader = Object.keys(headers).some((header) => header.startsWith('vizit-'));

  if (hasCustomHeader) {
    return Object.keys(headers).reduce(
      (acc, header) => {
        if (header.startsWith('vizit-')) {
          const key = header.replace('vizit-', '').replace('-', '_');
          acc[key] = headers[header];
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  }
  return undefined;
}

export const useExportPdpsCsvQuery = <
  TData = ExportPdpsCsvQuery,
  TError = ExportPdpsCsvV1WorkspacesWorkspaceIdPdpsExportGetError,
>(
  {
    workspace_id,
    sort_by,
    sort_direction,
    brand,
    product_category_id,
    category_id,
    listing_score_level,
    listing_score_at_ingest_level,
    search_item_id,
    search_name,
    search_brand,
    search_product_category,
  }: ExportPdpsCsvQueryVariables,
  options?: Omit<UseQueryOptions<ExportPdpsCsvQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<ExportPdpsCsvQuery, TError, TData>({
    queryKey: [
      'ExportPdpsCsv',
      {
        workspace_id,
        sort_by,
        sort_direction,
        brand,
        product_category_id,
        category_id,
        listing_score_level,
        listing_score_at_ingest_level,
        search_item_id,
        search_name,
        search_brand,
        search_product_category,
      },
    ],
    queryFn: async () => {
      const { data, response } = await exportPdpsCsvV1WorkspacesWorkspaceIdPdpsExportGet({
        path: { workspace_id },
        query: {
          sort_by,
          sort_direction,
          brand,
          product_category_id,
          category_id,
          listing_score_level,
          listing_score_at_ingest_level,
          search_item_id,
          search_name,
          search_brand,
          search_product_category,
        },
        parseAs: 'blob',
        throwOnError: true,
      });
      const customHeaders = getCustomHeaders(response);
      return customHeaders ? { ...customHeaders, data } : data;
    },
    ...options,
  });
};

useExportPdpsCsvQuery.getKey = ({
  workspace_id,
  sort_by,
  sort_direction,
  brand,
  product_category_id,
  category_id,
  listing_score_level,
  listing_score_at_ingest_level,
  search_item_id,
  search_name,
  search_brand,
  search_product_category,
}: ExportPdpsCsvQueryVariables) => [
  'ExportPdpsCsv',
  {
    workspace_id,
    sort_by,
    sort_direction,
    brand,
    product_category_id,
    category_id,
    listing_score_level,
    listing_score_at_ingest_level,
    search_item_id,
    search_name,
    search_brand,
    search_product_category,
  },
];

useExportPdpsCsvQuery.fetcher = (
  {
    workspace_id,
    sort_by,
    sort_direction,
    brand,
    product_category_id,
    category_id,
    listing_score_level,
    listing_score_at_ingest_level,
    search_item_id,
    search_name,
    search_brand,
    search_product_category,
  }: ExportPdpsCsvQueryVariables,
  _headers?: RequestInit['headers'],
): (() => Promise<ExportPdpsCsvQuery>) => {
  return async (): Promise<ExportPdpsCsvQuery> => {
    const { data, response } = await exportPdpsCsvV1WorkspacesWorkspaceIdPdpsExportGet({
      path: { workspace_id },
      query: {
        sort_by,
        sort_direction,
        brand,
        product_category_id,
        category_id,
        listing_score_level,
        listing_score_at_ingest_level,
        search_item_id,
        search_name,
        search_brand,
        search_product_category,
      },
      parseAs: 'blob',
      throwOnError: true,
    });
    const customHeaders = getCustomHeaders(response);
    return (customHeaders ? { ...customHeaders, data } : data) as ExportPdpsCsvQuery;
  };
};
